import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { http, formatDateTime } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Plus, X, CalendarBlank, Clock, MapPin, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Field, inputCls } from "@/pages/PipelinePage";
import SearchableContactSelect from "@/components/SearchableContactSelect";

export default function MeetingsPage() {
  const { t } = useI18n();
  const [meetings, setMeetings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const load = async () => {
    const [m, c, d] = await Promise.all([
      http.get("/meetings"), http.get("/contacts"), http.get("/deals")
    ]);
    setMeetings(m.data);
    setContacts(c.data);
    setDeals(d.data);
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const byDay = {};
    meetings.forEach((m) => {
      const key = new Date(m.start_time).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" });
      byDay[key] = byDay[key] || [];
      byDay[key].push(m);
    });
    Object.values(byDay).forEach(list => list.sort((a, b) => new Date(a.start_time) - new Date(b.start_time)));
    return byDay;
  }, [meetings]);

  const del = async (id) => {
    if (!window.confirm("Batalkan meeting?")) return;
    await http.delete(`/meetings/${id}`);
    toast.success("Meeting dihapus");
    load();
  };

  return (
    <AppLayout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 sm:gap-4 mb-5 sm:mb-8">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#64748B] font-bold">Schedule</div>
          <h1 className="font-display text-2xl sm:text-4xl lg:text-5xl tracking-tight mt-1 text-[#173042]">Jadwal Meeting</h1>
        </div>
        <button onClick={() => setShowModal(true)} data-testid="new-meeting-btn"
          className="self-start sm:self-auto bg-[#173B4D] hover:bg-[#112B38] text-white px-4 py-2 rounded-lg flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider shadow-xs transition-colors">
          <Plus size={16} weight="bold" /> <span>{t("action.newMeeting")}</span>
        </button>
      </div>

      {Object.keys(grouped).length === 0 && (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-8 sm:p-12 text-center shadow-2xs">
          <div className="text-base sm:text-lg font-bold text-[#173042]">{t("empty.title")}</div>
          <div className="text-xs sm:text-sm text-[#64748B] mt-1">{t("empty.desc")}</div>
        </div>
      )}

      <div className="space-y-6 sm:space-y-8" data-testid="meetings-list">
        {Object.entries(grouped).map(([day, list]) => (
          <div key={day}>
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-[#64748B] font-bold mb-3 flex items-center gap-1.5">
              <CalendarBlank size={15} className="text-[#173B4D]" /> <span>{day}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {list.map((m) => (
                <div key={m.id} data-testid={`meeting-${m.id}`} className="bg-white rounded-xl border border-[#E2E8F0] border-l-4 border-l-[#173B4D] p-4 sm:p-5 shadow-2xs hover:shadow-card-hover transition-shadow flex flex-col justify-between">
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-bold text-sm text-[#173042] leading-snug">{m.title}</div>
                      <button onClick={() => del(m.id)} className="p-1 rounded text-[#64748B] hover:text-red-600 hover:bg-red-50" title="Hapus"><Trash size={14} /></button>
                    </div>
                    <div className="flex items-center gap-1.5 mt-2.5 text-xs font-mono-data font-semibold text-[#0F7180]">
                      <Clock size={13} /> {new Date(m.start_time).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })} - {new Date(m.end_time).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}
                    </div>
                    {m.location && (
                      <div className="flex items-center gap-1.5 mt-2 text-xs text-[#64748B]">
                        <MapPin size={13} className="shrink-0 text-[#64748B]" /> <span className="truncate">{m.location}</span>
                      </div>
                    )}
                    {m.description && <div className="mt-2.5 text-xs text-[#64748B] line-clamp-2 leading-relaxed">{m.description}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {showModal && <MeetingModal contacts={contacts} deals={deals} onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); }} />}
    </AppLayout>
  );
}

function MeetingModal({ onClose, onSaved, contacts, deals }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    title: "", description: "", start_time: "", end_time: "",
    location: "", contact_id: "", deal_id: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, start_time: new Date(form.start_time).toISOString(), end_time: new Date(form.end_time).toISOString() };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      payload.title = form.title;
      payload.start_time = new Date(form.start_time).toISOString();
      payload.end_time = new Date(form.end_time).toISOString();
      await http.post("/meetings", payload);
      toast.success("Meeting dijadwalkan");
      onSaved();
    } catch { toast.error("Gagal membuat meeting"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-3 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl border border-[#E2E8F0] w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden page-enter" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 sm:px-6 py-3.5 sm:py-4 border-b border-[#E2E8F0] flex items-center justify-between bg-[#F8FAFC] shrink-0">
          <div>
            <h3 className="font-bold text-sm sm:text-base text-[#173042]">Meeting Baru</h3>
            <p className="text-[11px] sm:text-xs text-[#64748B] mt-0.5">Jadwalkan agenda meeting atau audit</p>
          </div>
          <button onClick={onClose} className="p-1 rounded text-[#64748B] hover:text-[#173042] hover:bg-[#E2E8F0]"><X size={18} weight="bold" /></button>
        </div>
        <form onSubmit={submit} className="flex flex-col flex-1 overflow-hidden" data-testid="meeting-form">
          <div className="p-4 sm:p-6 space-y-3.5 sm:space-y-4 overflow-y-auto flex-1">
            <Field label={t("common.title")} required>
              <input required data-testid="meeting-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} placeholder="Agenda Meeting" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <Field label={t("common.startTime")} required>
                <input required type="datetime-local" data-testid="meeting-start" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className={inputCls} />
              </Field>
              <Field label={t("common.endTime")} required>
                <input required type="datetime-local" data-testid="meeting-end" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className={inputCls} />
              </Field>
            </div>
            <Field label="Lokasi / Link">
              <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputCls} placeholder="Zoom link / alamat kantor" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <Field label="Kontak">
                <SearchableContactSelect
                  contacts={contacts}
                  value={form.contact_id}
                  onChange={(id) => setForm({ ...form, contact_id: id })}
                  placeholder="Cari nama kontak atau perusahaan..."
                />
              </Field>
              <Field label="Deal">
                <select value={form.deal_id} onChange={(e) => setForm({ ...form, deal_id: e.target.value })} className={inputCls}>
                  <option value="">- Pilih Deal -</option>
                  {deals.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Deskripsi">
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls + " min-h-[70px]"} placeholder="Rincian meeting..." />
            </Field>
          </div>
          <div className="px-4 sm:px-6 py-3 border-t border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-end gap-2.5 shrink-0">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-[#CBD5E1] text-xs font-semibold uppercase tracking-wider text-[#64748B] hover:text-[#173042] hover:bg-[#F8FAFC]">{t("action.cancel")}</button>
            <button disabled={saving} type="submit" data-testid="meeting-submit" className="bg-[#173B4D] hover:bg-[#112B38] text-white px-5 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider disabled:opacity-60 transition-colors shadow-xs">
              {saving ? "..." : t("action.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
